import { takeLatest, call, put, select } from 'redux-saga/effects'
import { getHiringTeamSessionMetadata } from '../apiModules/hiringTeamSessionMetadata'
import { userSignOut } from '../apiModules/sessions'
import * as sessionView from '../reducks/sessions'
import { hiringTeamId, memberProfileId, userRole } from '../selectors/currentUser'
import { getPreviousTopLevelHref } from '../containers/SessionHistory/selectors'
import { fetchMemberProfileHiringTeamEntitiesSaga, updateMemberProfileEntitySaga } from './memberProfiles'
import { updateImpersonatedMemberProfileEntitySaga } from './impersonatedMemberProfiles'
import { errorHandler } from '../helpers/errorHelpers'
import { showSystemError, hideSystemError } from '../components/SystemErrorRedux'

import { fetchCompanyAccountEntitySaga } from './companyAccounts'

function* fetchHiringTeamsSaga() {
  const memberProfile = yield select(memberProfileId)

  try {
    yield put(hideSystemError())

    const response = yield call(fetchMemberProfileHiringTeamEntitiesSaga, memberProfile)
    yield put(sessionView.fetchHiringTeamsSuccess(response))
  } catch (err) {
    const { error, status } = yield call(errorHandler, err)
    yield put(sessionView.fetchHiringTeamError(error))

    // Don't show system error if not found and if no members on account
    if (status !== 404 && memberProfile !== '') {
      yield put(showSystemError(error))
    }
  }
}

function* fetchMetadataSaga() {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(getHiringTeamSessionMetadata, hiringTeam)
    yield put(sessionView.fetchMetadataSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(sessionView.fetchMetadataError(error))

    yield put(showSystemError(error))
  }
}

function* fetchCompanyAccountSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const response = yield call(fetchCompanyAccountEntitySaga, meta.companySlug)
    yield put(sessionView.fetchCompanyAccountSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(sessionView.fetchCompanyAccountError(error))

    yield put(showSystemError(error))
  }
}

const splitUrl = path => {
  return path.split('/')
}

const isTopLevelHref = href => {
  const urlParts = splitUrl(href)
  const lastPart = urlParts[urlParts.length - 1]

  return ['candidates', 'positions', 'interview_requests', 'shortlist', 'analytics', 'applications', 'dashboard'].includes(lastPart)
}

const redirectToPreviousTopLevelList = path => {
  const urlParts = splitUrl(path)
  return urlParts.slice(0, 5).join('/')
}

const redirectToCandidatePositionList = path => {
  const urlParts = splitUrl(path)
  return `${urlParts.slice(0, 3).join('/')}/candidates/positions`
}

const redirectToCandidateList = path => {
  const urlParts = splitUrl(path)
  return `${urlParts.slice(0, 3).join('/')}/candidates`
}

const redirectToListView = (path, previousPath) => {
  const urlParts = splitUrl(path)
  if (urlParts.includes('positions') && urlParts.includes('candidates')) {
    return redirectToCandidatePositionList(path)
  }

  if (previousPath === null) {
    return redirectToCandidateList(path)
  }

  if (!isTopLevelHref(previousPath)) {
    return redirectToPreviousTopLevelList(previousPath)
  }

  return previousPath
}

function* switchHiringTeamSaga({ meta }) {
  try {
    yield put(hideSystemError())
    const memberProfile = yield select(memberProfileId)
    const authRole = yield select(userRole)

    // Reset the window location to the IR list to force a reload with the new hiring team
    // We need to reset because the current URL might be unauthorized for the new hiring team
    // The parameter current_hiring_team_id has been added in order to impersonate a member of a hiring team
    // when the current user is an OZ Admin. This is due to how god mode impersonations have been implemented,
    // and this will need to change in the future should be change the impersonation implementation.
    // FIXME Needs to be smarter when we add more sections to this project

    if (authRole === 'offerzen_user') {
      const { result } = yield call(updateImpersonatedMemberProfileEntitySaga, memberProfile, meta.data.current_hiring_team_id)

      if (isTopLevelHref(window.location.pathname)) {
        const href = new URL(window.location.origin + window.location.pathname)
        href.searchParams.set('current_hiring_team_id', result.current_hiring_team.id)

        window.location = href
      } else {
        const previousPath = yield select(getPreviousTopLevelHref)
        const path = redirectToListView(window.location.pathname, previousPath)
        const href = new URL(window.location.origin + path)
        href.searchParams.set('current_hiring_team_id', result.current_hiring_team.id)

        window.location = href
      }
    } else {
      yield call(updateMemberProfileEntitySaga, memberProfile, meta.data)

      if (isTopLevelHref(window.location.pathname)) {
        window.location = window.location.pathname
      } else {
        const previousPath = yield select(getPreviousTopLevelHref)
        window.location = redirectToListView(window.location.pathname, previousPath)
      }
    }
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(showSystemError(error))
  }
}

function* signOutSaga() {
  try {
    yield put(hideSystemError())
    yield call(userSignOut)

    // Send the user back to the homepage
    window.location = '/'
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(showSystemError(error))
  }
}

export default function* watchSessionsSagas() {
  yield takeLatest(sessionView.FETCH_HIRING_TEAMS, fetchHiringTeamsSaga)
  yield takeLatest(sessionView.FETCH_METADATA, fetchMetadataSaga)
  yield takeLatest(sessionView.FETCH_COMPANY_ACCOUNT, fetchCompanyAccountSaga)
  yield takeLatest(sessionView.SWITCH_HIRING_TEAM, switchHiringTeamSaga)
  yield takeLatest(sessionView.SIGN_OUT, signOutSaga)
}
